<template>
  <section :class="{ 'in-call': callLocalTrack }" >
    <div class="header" :style="{
      borderTopColor: visualSettings.channelHeaderBackgroundColor,
      borderTopWidth: visualSettings.channelHeaderBorderWidth,
      borderBottomColor: visualSettings.channelHeaderBorderColor, 
      borderBottomWidth: visualSettings.channelHeaderBorderWidth,
      backgroundColor: visualSettings.channelHeaderBackgroundColor
    }">
      <div class="v-align">
        <div class="close">
          <button @click="back()" aria-label="Close">
            <fa-icon 
              :icon="['fas', 'angle-left']" 
              size="2x"
            />
          </button>
        </div>
        <div class="label" :style="{ color: visualSettings.channelHeaderLabelColor }">{{activeChannel.name}}</div>
        <div class="banner" v-if="activeChannel.banner">
          <img ref="banner" :src="activeChannel.banner" />
        </div>
        <div class="actions" v-if="chatCallsEnabled">
          <button class="error" v-if="callEnabled" @click="call({ audio: true });" :disabled="callLocalTrack">
            <fa-icon 
              :icon="['fas', 'phone']" 
              size="2x"
            />
          </button>
          <button class="error" v-if="callEnabled" @click="call({ audio: true, video: true });" :disabled="callLocalTrack">
            <fa-icon 
              :icon="['fas', 'video']" 
              size="2x"
            />
          </button>
        </div>
      </div>
    </div>
    
    <div class="pinned" v-if="pinnedMessages">
      <ul>
        <li 
          v-for="pinnedMessage in pinnedMessages"
          :key="pinnedMessage.actionId"
          :style="{}"
        >
          <span>{{pinnedMessage.value}}</span>
          <span>
            <button @click="clearPin(pinnedMessage.actionId)" aria-label="Clear pin">
              <fa-icon
                :icon="['fas', 'eye-slash']"  
                size="lg"
              />
            </button>
          </span>
        </li>
      </ul>
    </div>

    <div class="load-error" v-if="activeChannel.error">
      <div class="v-align">
        <div class="inner">
          <div class="notification">
            <Notification
              :message="'Unable to load messages'"
              :icon="['fas', 'exclamation-circle']"
              type="info"
            />
          </div>
          <button @click="reload" :disabled="processing" aria-label="Reload">
            <span class="spinner" v-if="processing">
              <fa-icon
                icon="spinner" 
                size="1x"
                :spin="true"
              />
            </span>
            Retry
          </button>
        </div>
      </div>
    </div>
    
    <div class="camera">
      <div class="inner">
        <div class="remote" ref="remote"></div>
        <div class="local" ref="local"></div>
        <div class="notification-block" :class="{ hasCallParticipants }">
          <div class="v-align">
            <div class="inner">
              <fa-icon 
                v-if="hasCallParticipants"
                :icon="['fas', 'phone']" 
                size="2x"
              />
              <Notification
                v-if="!hasCallParticipants"
                :message="`Waiting for ${activeChannel.name} to join...`"
                :icon="['fas', 'spinner']" 
                type="light"
              />
            </div>
          </div>
        </div>
        <div class="controls">
          <div class="inner">
            <button @click="toggleMute()" :class="{ 'error': isMuted }">
              <fa-icon 
                :icon="['fas', isMuted ? 'microphone-slash' : 'microphone']" 
                size="2x"
              />
            </button>
            <button @click="toggleVideo()" :class="{ 'error': isVideoHidden }">
              <fa-icon 
                :icon="['fas', isVideoHidden ? 'video-slash' : 'video']" 
                size="2x"
              />
            </button>
            <button @click="hangUp({})" class="error">
              <fa-icon 
                :icon="['fas', 'phone-slash']" 
                size="2x"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <virtual-list
      v-if="activeChannel.messages.length > 0"
      class="messages"
      ref="vsl"
      :data-key="'id'"
      :data-sources="groupDays"
      :data-component="messageVirtualItem"
      :estimate-size="78"
      :keeps="25"
      :start="10000"
      :bottom-threshold="25"
      @totop="loadNextPage"
      @scroll="onScroll"
      @tobottom="stickToBottom"
    />
    <div class="fetching" v-if="fetching">
      <div class="content">
        <div class="inner">
          <Notification 
            message="Loading Messages..."
            :icon="['fas', 'spinner']" 
            type="info"
          />
        </div>
      </div>
      <div class="spacer"></div>
    </div>
    <div class="notifications" v-if="!activeChannel.sticky && activeChannel.notificationCount > 0">
      <div class="content">
        <div class="inner" @click="scrollToBotton">
          <Notification 
            :message="`${activeChannel.notificationCount} New Message${activeChannel.notificationCount > 1 ? 's' : ''}`"
            :icon="['fas', 'envelope']"
            type="info"
          />
        </div>
      </div>
      <div class="spacer"></div>
    </div>
    <p v-if="activeChannel.init && activeChannel.messages.length === 0" class="empty">{{emptyText}}</p>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import VirtualList from 'vue-virtual-scroll-list';

import Notification from '@/components/Notification';
import MessageVirtualItem from '@/components/MessageVirtualItem';

export default {
  name: 'History',
  components: {
    'virtual-list': VirtualList,
    Notification
  },
  data() {
    return {
      messageVirtualItem: MessageVirtualItem,
      scrollLoaded: false,
      processing: false,
      fetching: false,
      updating: false,
      sticky: true,
      muted: false
    }
  },
  computed: {
    pinnedMessages() {
      const actionIds = [];
      const actions = [];
      [...this.activeChannel.actions].reverse().forEach((action) => {
        if (!actionIds.includes(action.actionId)) {
          actionIds.push(action.actionId);
          actions.push(action);
        }
      });

      return actions
        .reverse()
        .filter((action) => action.type === 'pin')
        .filter((action) => !this.clearedPins.includes(action.actionId));
    },
    callEnabled() {
      return this.activeChannel.type === 'direct' && this.activeChannel.id !== this.activeUser.channelId;
    },
    isMuted() {
      return this.muted || !this.callLocalTrack;
    },
    isVideoHidden() {
      return !this.showingVideo || !this.callLocalTrack;
    },
    emptyText() {
      switch(this.activeChannel.type) {
        case 'direct':
          return (this.activeChannel.id === this.activeUser.channelId)
            ? 'This is your private channel. Only you can see these notes.'
            : `This is the very beginning of your direct message history with ${this.activeChannel.name}...`;
        case 'group':
          return `Be the first to send a message to the ${this.activeChannel.name} group...`;
      }
      return '';
    },
    groupDays() {
      let lastDay = null;
      const messageIds = [];
      const messages = [];
      this.activeChannel.messages.forEach((message) => {
        if (messageIds.includes(message.id)) {
          return;
        }
        const currentDay = this.$moment(message.id/10000).format('YYYY-MM-DD');
        if (lastDay !== currentDay) {
          lastDay = currentDay;
          message.showDay = true;
        } else {
          message.showDay = false;
        }
        messageIds.push(message.id);
        messages.push(message);
      });
      messages[messages.length-1].lastDay = true;
      return messages;
    },
    ...mapGetters([
      'visualSettings',
      'videoConnectOptions',
      'hasVideoOrAudioInput',
      'hasCallParticipants',
      'chatCallsEnabled'
    ]),
    ...mapState([
      'activeChannel',
      'activeUser',
      'clearedPins',
      'videoToken',
      'mediaDevices',
      'callRoom',
      'localVideoTrack',
      'callLocalTrack',
      'showingVideo',
      'callParticipants'
    ])
  },
  methods: {
    ...mapMutations([
      'setLocalVideoTrack'
    ]),
    ...mapActions([
      'loadMediaDevices',
      'sendMessage',
      'getTracks',
      'initCall',
      'hangUp',
      'showVideo'
    ]),
    async call({ audio, video }) {
      if (!this.chatCallsEnabled) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio/Video Calls Are Disabled',
          text: 'You cannot make audio or video calls as the features is currently disabled. Please contact the chat administrator.',
          duration: 5000
        });
        return;
      }
      await this.loadMediaDevices();
      if (this.hasVideoOrAudioInput) {
        this.muted = !audio;
        this.initCall({ audio, video });
      } else {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Audio/Video Not Found',
          text: 'Unable to find audio or video devices to begin call. Please check your browser settings.',
          duration: 5000
        });
      }
    },
    async toggleMute() {
      this.muted = !this.muted;
      (await this.getTracks(this.callRoom.localParticipant)).forEach((track) => {
        if (track.kind === 'audio') {
          if (this.muted) {
            track.disable();
          } else {
            track.enable();
          }
        }
      });
    },
    async toggleVideo() {
      this.showVideo({ show: !this.showingVideo });
    },
    clear(actionId) {
      this.clearPin(actionId);
    },
    back() {
      this.setSidebarOpen(true);
    },
    async loadNextPage() {
      if (this.fetching || this.activeChannel.doneFetching) {
        return;
      }

      this.setChannelSticky({
        channelId: this.activeChannel.id,
        isSticky: false
      });
      this.fetching = true;
      const oldLength = this.activeChannel.messages.length;
      await this.addChannelMessagesPage(this.activeChannel.id);

      this.$nextTick(() => {
        const newLength = this.activeChannel.messages.length;
        if (this.$refs.vsl) {
          this.$refs.vsl.scrollToIndex(newLength - oldLength);
        }
        this.fetching = false;
      });
    },
    scrollToBotton() {
      if (this.$refs.vsl) {
        this.$refs.vsl.scrollToBottom();
      }
    },
    onScroll() {
      if (this.activeChannel.sticky && !this.updating) {
        this.setChannelSticky({
          channelId: this.activeChannel.id,
          isSticky: false
        });
      }
    },
    stickToBottom() {
      this.setChannelSticky({
        channelId: this.activeChannel.id,
        isSticky: true
      });
    },
    async reload() {
      this.processing = true;
      await this.addChannelMessagesPage(this.activeChannel.id);
      this.processing = false;
    },
    ...mapMutations([
      'setSidebarOpen',
      'setChannelSticky',
      'setCallRemoteRef',
      'setCallLocalRef',
      'setCallLocalTrack',
      'clearPin'
    ]),
    ...mapActions([
      'addChannelMessagesPage'
    ])
  },
  watch: {
    activeChannel() {
      this.fetching = true;
      if (this.$refs.vsl) {
        this.$refs.vsl.scrollToBottom();
      }
      this.fetching = false;
    }
  },
  updated() {
    this.updating = true;
    if (this.$refs.vsl && this.activeChannel.sticky) {
      this.$nextTick(() => {
        this.scrollToBotton();
        this.stickToBottom();
        this.updating = false;
      });
    }
  },
  mounted() {
    this.setCallRemoteRef(this.$refs.remote);
    this.setCallLocalRef(this.$refs.local);
  }
}
</script>

<style scoped lang="scss">
section {
  position: relative;
  width: 100%;
  height: 100%;
}

.header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 54px;
  width: 100%;
  box-sizing: border-box;
  padding: 0 32px;
  border-top: 1px solid #fff;
  border-bottom: 1px solid #eaebef;

  @media only screen and (max-width: 768px) {
    padding-left: 12px;
    padding-right: 12px;
  }

  > .v-align {
    display: table;
    width: 100%;
    height: 100%;
  }

  h5 {
    font-size: 13.6px;
    margin: 0;
  }

  .close {
    display: none;
    vertical-align: middle;
    padding: 0 10px 0 0;
    width: 1px;

    button {
      border: 0;
      width: 33px;
      height: 33px;
      background: #fff;
      border-radius: 8px;
      cursor: pointer;
      background: rgba(234, 235, 239, 0.5);

      &:hover {
        background: #eaebef;
      }
    }

    @media only screen and (max-width: 768px) {
      display: table-cell;
    }
  }

  .label {
    display: table-cell;
    vertical-align: middle;
    width: 99%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    font-size: 13.6px;
    font-weight: bold;
    margin: 0;
  }

  .banner {
    display: table-cell;
    vertical-align: middle;
    font-size: 0;

    img {
      height: 32px;
    }
  }

  .actions {
    display: table-cell;
    vertical-align: middle;
    font-size: 0;
    white-space: nowrap;

    button {
      border: 0;
      width: 33px;
      height: 33px;
      border-radius: 8px;
      font-size: 8px;
      cursor: pointer;
      background: rgba(234, 235, 239, 0.5);
      margin-left: 6px;
      display: inline-block;

      &:hover {
        background: #eaebef;
      }
      
      &:first-child {
        margin-left: 0;
      }
    }
  }
}

.messages {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 0;
  max-height: calc(100% - 54px);
  box-sizing: border-box;
  overflow-y: scroll;
  padding: 20px 20px 0;
  transition: opacity 250ms ease-out;

  @media only screen and (max-width: 768px) {
    padding-left: 16px;
    padding-right: 16px;
  }
}

.pinned {
  position: absolute;
  left: 0;
  right: 0;
  top: 54px;
  z-index: 2;

  ul {
    margin: 10px 30px 0 20px;
    padding: 0;

    @media only screen and (max-width: 768px) {
      margin-left: 16px;
      margin-right: 16px;
    }

    li {
      display: table;
      width: 100%;
      list-style: none;
      padding: 10px 12px 12px;
      border-radius: 8px;
      box-sizing: border-box;
      box-shadow: 0 3px 7px rgb(60 40 40 / 7%);
      background: #d3eaec;
      border: 1px solid #c6dcde;
      margin: 10px 0 0 0;

      &:first-child {
        margin-top: 0;
      }
         
      > * {
        display: table-cell;
        vertical-align: middle;
      }

      span {

        &:last-child {
          padding: 0 0 0 10px;
          width: 1%;
          vertical-align: top;
        }
      }

      button {
        width: 30px;
        height: 30px;
        border-radius: 4px;
        font-size: 11.7px;
        border: 0;
        padding: 0;
        cursor: pointer;
        color: rgba(47, 49, 56, 0.6);
        background: none;
        
        transition: color 250ms ease-out,
          background 250ms ease-out;

        &:hover {
          background: none;
          color: #2f3138;
        }
      }
    }
  }
}

p.empty {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  font-size: 16px;
  padding: 0 34px;
  display: block;

  @media only screen and (max-width: 768px) {
    padding-left: 27px;
    padding-right: 27px;
  }
}

.fetching,
.notifications {
  position: absolute;
  display: flex;
  top: 64px;
  left: 34px;
  right: 34px;
  z-index: 10;
  text-align: center;
  padding: 10px 0;

  @media only screen and (max-width: 768px) {
    left: 27px;
    right: 27px;
  }

  .content {
    flex: 1;

    > .inner {
      display: inline-block;
      border-radius: 4px;
      background: #fff;
      cursor: pointer;
    }
  }

  .spacer {
    flex: 0 0 auto;
    opacity: 0;
    font-size: 0;
    height: 30px;
    scrollbar-color: rgba(0,0,0,0) rgba(0,0,0,0);
    position: relative;
    overflow-y: scroll;

    @media only screen and (max-width: 768px) {
      display: none;
    }
  }
}

.notifications {
  bottom: 51px;
  top: auto;
  height: 0;
  padding: 0;
}

.load-error {
  position: absolute;
  top: 0;
  left: 20px;
  right: 20px;
  bottom: 0;

  @media only screen and (max-width: 768px) {
    left: 16px;
    right: 16px;
  }

  > .v-align {
    display: table;
    margin: 0 auto;
    height: 100%;

    > .inner {
      display: table-cell;
      vertical-align: middle;
    }
  }

  .notification {
    display: inline-block;
    margin: -1px 0 0 0;

    @media only screen and (max-width: 768px) {
      display: block;
      margin-top: 0;
    }
  }

  button {
    margin: 0 0 0 10px;
    font-size: 16px;
    font-family: "Poppins";
    text-align: center;
    height: 40px;
    padding: 0 15px;
    color: #fff;
    box-sizing: border-box;
    border-radius: 4px;
    border: 0;
    background: rgb(2, 2, 2);
    opacity: 0.5;
    cursor: pointer;
    transition: opacity 250ms ease-out;

    @media only screen and (max-width: 768px) {
      margin-top: 10px;
      margin-left: 0;
      width: 100%;
    }

    &:hover {
      opacity: 0.7;
    }

    .spinner {
      position: relative;
      left: 0;
      width: 20px;
      margin: 0 0 0 0;

      @media only screen and (max-width: 768px) {
        left: -20px;
        margin-right: -20px;
      }
    }

    &:disabled {
      cursor: default;
    }
  }
}

.camera {
  position: fixed;
  top: 73px;
  right: 300px;
  left: 270px;
  bottom: 80px;
  z-index: 2;
  display: none;
  background: rgba(0,0,0,1);
  border-radius: 4px;
  box-shadow: 0 20px 20px rgba(255,255,255,1);
  // max-height: 300px;

  @media only screen and (max-width: 1024px) and (min-height: 400px) {
    right: 20px;
    bottom: 50%;
  }

  @media only screen and (max-width: 768px) {
    left: 16px;
  }

  @media only screen and (max-width: 768px) and (min-height: 400px) {
    right: 16px;
  }

  > .inner {
    position: relative;
    // padding-bottom: 56.25%;
    height: 100%;
    box-sizing: border-box;
  }

  .notification-block {
    position: absolute;
    top: 0;
    right: 20px;
    left: 20px;
    bottom: 0;

    .v-align {
      display: table;
      margin: 0 auto;
      height: 100%;
      
      > .inner {
        display: table-cell;
        vertical-align: middle;
      }
    }
  }

  .controls {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 2;

    > .inner {
      padding: 4px;
      border-radius: 10px;
      background: rgba(255,255,255,0.25);
    }

    button {
      border: 0;
      width: 33px;
      height: 33px;
      border-radius: 8px;
      font-size: 8px;
      cursor: pointer;
      margin: 0 6px 0 0;
      background: rgba(234, 235, 239, 0.5);
      transition: background 250ms ease-out;

      &:hover {
        background: #eaebef;
      }

      &:last-child {
        margin-right: 0;
      }

      &.error {
        background: #E54D42;

        &:hover {
          background-color: rgba(229,77,66,0.8);
        } 
      }
    }
  }

  .remote {
    position: absolute;
    box-sizing: border-box;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    font-size: 0;
    z-index: 1;
  }

  .local {
    position: absolute;
    box-sizing: border-box;
    top: 12px;
    right: 12px;
    width: 120px;
    height: 67.5px;
    font-size: 0;
    z-index: 2;
  }

  ::v-deep {

    video {
      position: relative;
      width: 100%;
      height: 100%;
      object-fit: contain;  
    }
  }
}

.in-call {

  .messages {
    width: 300px;
    left: auto;

    @media only screen and (max-width: 1024px) and (min-height: 400px) {
      left: 0;
      width: auto;
    }
  }

  .camera {
    display: block;
  }
}
</style>

<style lang="scss">
.remote,
.local {

  video {
    position: absolute;
    width: 100%;
    height: 100%;
    border: 2px solid rgba(0,0,0,0.25);
    border-radius: 4px;
    object-fit: contain;
    background: rgba(0,0,0,0.1);
  }
}
</style>